import { Link } from "react-router-dom";
import { energyKwh } from "../../lib/publicOrder";
import { perKwh } from "../../lib/tariff";
import type { PublicOrderDetail } from "../../lib/types";
import { Copyable } from "../ui/Copy";
import { MicroLabel } from "../ui/Text";

/**
 * Where, at what price, and the handles an operator carries elsewhere — the order
 * panel's side column, beside the money as uptime sits beside a station's facts.
 *
 * The settlement reference is here rather than folded away with the payment rows
 * because it is the one string an operator takes out of this panel: it is what
 * gets typed into the merchant portal to return money by hand.
 */

const LINK =
  "text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline";

export function OrderParty({ row }: { readonly row: PublicOrderDetail }) {
  // The attempt the money came in on: the newest approved one, since a retried
  // settlement leaves earlier attempts behind it.
  const paidWith = [...row.payments]
    .reverse()
    .find((payment) => payment.status === "approved" || payment.status === "refunded");

  return (
    <aside className="flex min-w-0 flex-col gap-6 border-t border-rule pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
      <section aria-label="Charged at" className="flex flex-col gap-1.5">
        <MicroLabel>Charged at</MicroLabel>
        <span className="text-section font-semibold text-ink">{row.chargePointId}</span>
        <span className="text-data text-ink-muted">
          EVSE {row.evseId} · {row.depotName}
        </span>
        <span className="text-data text-ink-muted">
          {perKwh(row.priceMinorPerKwh, row.currency)} · {row.tariffName}
        </span>
        <div className="pt-1">
          <Link to={`/fleet/${encodeURIComponent(row.chargePointId)}`} className={LINK}>
            Station
          </Link>
        </div>
      </section>

      {row.settlementRef === null ? null : (
        <section aria-label="Settlement" className="flex flex-col gap-1.5">
          <Copyable
            label="Settlement reference"
            value={row.settlementRef}
            hint={
              paidWith === undefined
                ? "The reference to search for in the merchant portal."
                : `Paid with ${paidWith.paymentOption ?? paidWith.provider}. The reference to search for in the merchant portal.`
            }
          />
        </section>
      )}

      <section aria-label="Session" className="flex flex-col gap-1.5">
        <MicroLabel>Session</MicroLabel>
        {row.session === null ? (
          // Either the driver never plugged in, or the station never accepted the
          // start — the difference between an expired hold and a refund.
          <span className="text-data text-ink-faint">None opened</span>
        ) : (
          <>
            <span className="text-data text-ink">
              {row.session.energyWh === null
                ? `${row.session.state}, energy not committed yet`
                : `${row.session.state} · ${energyKwh(row.session.energyWh)} kWh`}
            </span>
            <div className="pt-1">
              <Link
                to={`/sessions?session=${encodeURIComponent(row.session.id)}`}
                className={LINK}
              >
                Transaction {row.session.ocppTransactionId}
              </Link>
            </div>
          </>
        )}
      </section>
    </aside>
  );
}
